// client/src/components/wetakeover/CampaignWizard.jsx
import React, { useState } from 'react';
import ProgressIndicator from './ProgressIndicator';
import PricingPreview from './PricingPreview';
import TierSelection from './TierSelection';
import CreatorSlider from './CreatorSlider';
import QRTSlider from './QRTSlider';
import ReviewOrder from './ReviewOrder';
import WalletConnect from './WalletConnect';
import TelegramSetup from './TelegramSetup';
import { PRICING } from './PricingPreview';

const CampaignWizard = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [wizardData, setWizardData] = useState({
    tier: null,
    numCreators: 40,
    qrtsPerCreator: 1,
    projectDetails: {
      projectName: '',
      twitterHandle: '',
      briefingMaterial: '',
      instructions: '',
    },
    paymentTxHash: null,
    telegramHandle: '',
    orderId: null,
  });

  const totalSteps = 6;

  const calculateTotal = () => {
    if (!wizardData.tier) return 0;

    const pricing = PRICING[wizardData.tier];
    const totalQRTs = wizardData.numCreators * wizardData.qrtsPerCreator;
    return totalQRTs * pricing.qrtCost;
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return wizardData.tier !== null;
      case 2:
        return wizardData.numCreators >= 1 && wizardData.numCreators <= 150;
      case 3:
        return wizardData.qrtsPerCreator >= 1 && wizardData.qrtsPerCreator <= 10;
      case 4:
        return (
          wizardData.projectDetails.projectName &&
          wizardData.projectDetails.twitterHandle &&
          wizardData.projectDetails.instructions
        );
      case 5:
        return wizardData.paymentTxHash !== null;
      case 6:
        return wizardData.telegramHandle.length > 0;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (canProceed() && currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
      // Scroll to top of wizard
      document.getElementById('wizard-container')?.scrollIntoView({
        behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'start',
      });
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      // Scroll to top of wizard
      document.getElementById('wizard-container')?.scrollIntoView({
        behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'start',
      });
    }
  };

  const handlePaymentSuccess = (txHash) => {
    // Generate order ID
    const orderId = `WTO-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    setWizardData({
      ...wizardData,
      paymentTxHash: txHash,
      orderId,
    });
  };

  const handlePaymentError = (error) => {
    console.error('[CampaignWizard] Payment error:', error);
    // Could show error toast here
  };

  const handleComplete = async () => {
    // Final submission logic would go here
    console.log('[CampaignWizard] Campaign complete:', wizardData);
    // Could redirect to dashboard or show success modal
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <TierSelection
            selectedTier={wizardData.tier}
            onSelect={(tier) => setWizardData({ ...wizardData, tier })}
          />
        );
      case 2:
        return (
          <CreatorSlider
            count={wizardData.numCreators}
            onCountChange={(count) => setWizardData({ ...wizardData, numCreators: count })}
            tier={wizardData.tier}
          />
        );
      case 3:
        return (
          <QRTSlider
            qrtsPerCreator={wizardData.qrtsPerCreator}
            onQRTChange={(qrts) => setWizardData({ ...wizardData, qrtsPerCreator: qrts })}
            numCreators={wizardData.numCreators}
          />
        );
      case 4:
        return (
          <ReviewOrder
            tier={wizardData.tier}
            numCreators={wizardData.numCreators}
            qrtsPerCreator={wizardData.qrtsPerCreator}
            projectDetails={wizardData.projectDetails}
            onProjectDetailsChange={(details) =>
              setWizardData({ ...wizardData, projectDetails: details })
            }
          />
        );
      case 5:
        return (
          <WalletConnect
            totalAmount={calculateTotal()}
            onPaymentSuccess={handlePaymentSuccess}
            onPaymentError={handlePaymentError}
          />
        );
      case 6:
        return (
          <TelegramSetup
            telegramHandle={wizardData.telegramHandle}
            onHandleChange={(handle) => setWizardData({ ...wizardData, telegramHandle: handle })}
            orderId={wizardData.orderId}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div
      id="wizard-container"
      className="max-w-6xl mx-auto rounded-[40px] md:rounded-[56px] bg-zinc-950/80 backdrop-blur-xl shadow-[0_30px_120px_rgba(0,0,0,0.75)] border border-white/10 p-6 md:p-10 lg:p-12 relative overflow-hidden"
    >
      {/* Glow Overlays */}
      <div className="absolute -top-40 -left-40 h-[520px] w-[520px] rounded-full bg-white/10 blur-[120px] pointer-events-none" />
      <div className="absolute -bottom-40 -right-40 h-[520px] w-[520px] rounded-full bg-yellow-300/8 blur-[120px] pointer-events-none" />

      {/* Content */}
      <div className="relative z-10">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-extrabold text-white mb-2">
            Build Your Campaign
          </h1>
          <p className="text-white/60">Configure your campaign in 6 simple steps</p>
        </div>

        {/* Progress Indicator */}
        <ProgressIndicator currentStep={currentStep} totalSteps={totalSteps} />

        {/* Main Content Grid */}
        <div className={`grid grid-cols-1 ${currentStep !== 4 ? 'lg:grid-cols-3' : ''} gap-8 mb-8`}>
          {/* Left: Step Content (2/3 width on desktop, full width on step 4) */}
          <div className={currentStep !== 4 ? 'lg:col-span-2' : ''}>{renderStep()}</div>

          {/* Right: Pricing Preview (1/3 width, sticky on desktop) - Hide on step 4 */}
          {currentStep !== 4 && (
            <div className="hidden lg:block">
              <PricingPreview
                tier={wizardData.tier}
                numCreators={wizardData.numCreators}
                qrtsPerCreator={wizardData.qrtsPerCreator}
              />
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between gap-4 pt-8 border-t border-white/10">
          {/* Back Button */}
          {currentStep > 1 && (
            <button
              onClick={handleBack}
              className="px-8 py-4 rounded-[32px] bg-white/5 hover:bg-white/10 border border-white/10 font-medium transition-colors flex items-center gap-2"
            >
              <span>←</span>
              <span>Back</span>
            </button>
          )}

          {/* Spacer */}
          {currentStep === 1 && <div />}

          {/* Next/Complete Button */}
          <button
            onClick={currentStep === totalSteps ? handleComplete : handleNext}
            disabled={!canProceed()}
            className="px-8 py-4 rounded-[32px] bg-yellow-300 hover:bg-yellow-400 active:bg-yellow-500 disabled:bg-yellow-300/30 disabled:cursor-not-allowed text-black font-bold transition-colors flex items-center gap-2 ml-auto"
          >
            <span>
              {currentStep === totalSteps ? 'Complete Setup' : currentStep === 5 ? 'Continue to Setup' : 'Continue'}
            </span>
            {currentStep < totalSteps && <span>→</span>}
          </button>
        </div>
      </div>

      {/* Mobile: Sticky bottom pricing - Hide on step 4 */}
      {currentStep !== 4 && (
        <PricingPreview
          tier={wizardData.tier}
          numCreators={wizardData.numCreators}
          qrtsPerCreator={wizardData.qrtsPerCreator}
          isMobile={true}
        />
      )}
    </div>
  );
};

export default CampaignWizard;
